
import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Share2, Copy, CheckCircle } from "lucide-react";
import { toast } from "sonner";

interface SharePuxadasDialogProps {
  animalNome: string;
  centenas: string[];
  milhares: string[];
  puxadas: string[];
}

const SharePuxadasDialog: React.FC<SharePuxadasDialogProps> = ({
  animalNome,
  centenas,
  milhares,
  puxadas
}) => {
  const [copied, setCopied] = useState(false);

  const generateShareText = () => {
    const text = `🎯 PUXADAS DO ${animalNome.toUpperCase()} 🎯

🔁 BICHOS QUE PUXAM:
${puxadas.join(' • ')}

🎲 CENTENAS GERADAS:
${centenas.join(' • ')}

💥 MILHARES GERADOS:
${milhares.join(' • ')}

🚀 Gerado pelo AI Bicho Oracle - Sua IA do Jogo do Bicho!
📱 Acesse: ${window.location.origin}`;
    
    return text;
  };

  const handleCopy = async () => {
    try {
      const text = generateShareText();
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Puxada copiada para a área de transferência!");
      
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Erro ao copiar. Tente novamente.");
    }
  };

  const handleShare = async () => {
    const text = generateShareText();
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Puxadas do ${animalNome}`,
          text: text,
        });
        toast.success("Puxadas compartilhadas com sucesso!");
      } catch (error) {
        // User cancelled sharing
        console.log('Share cancelled');
      }
    } else {
      // Fallback para navegadores sem suporte ao Web Share API
      handleCopy();
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white">
          <Share2 className="mr-2 h-4 w-4" />
          📤 Compartilhar Puxadas
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Compartilhar Puxadas do {animalNome}
          </DialogTitle>
          <DialogDescription>
            Compartilhe essas puxadas com seus amigos ou copie para usar em outro lugar.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="bg-muted p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Preview:</h4>
            <div className="text-sm whitespace-pre-line max-h-48 overflow-y-auto">
              {generateShareText()}
            </div>
          </div>
          
          <div className="flex flex-col gap-2">
            <Button onClick={handleShare} className="w-full">
              <Share2 className="mr-2 h-4 w-4" />
              Compartilhar
            </Button>
            
            <Button 
              onClick={handleCopy} 
              variant="outline" 
              className="w-full"
              disabled={copied}
            >
              {copied ? (
                <>
                  <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                  Copiado!
                </>
              ) : (
                <>
                  <Copy className="mr-2 h-4 w-4" />
                  Copiar Texto
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SharePuxadasDialog;
